import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { updateProfile, updateEmail, updatePassword } from "firebase/auth";
import { doc, updateDoc, getDoc, onSnapshot } from "firebase/firestore";
import { requestToJoinSchool } from "../shared/joinRequests";
import { auth, firestore } from "../shared/FirebaseConfig";
import { UserContext } from "../shared/UserContext";
import { useClassroomMode } from "../shared/classroomMode";
import { GRADE_SPAN_PRESETS } from "../shared/gradeModel";
import { removeSampleClassroom } from "../shared/sampleData";
import SchoolDropdown from "./SchoolDropdown";
import AddSchoolModal from "./AddSchoolModal";
import { ManageFollowing } from "./FollowedSignOuts";
import LunchSettings from "./LunchSettings";
import SafetySettings from "./SafetySettings";
import SupportSettings from "./SupportSettings";
import "./Settings.css";

// Distinct, legible preset colors so each lending tracker is glanceable for students.
const TRACKER_COLORS = ["#2563eb", "#16a34a", "#dc2626", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];

const SETTINGS_TABS = [
  { id: "account",   label: "Account" },
  { id: "signout",   label: "Sign-Out" },
  { id: "library",   label: "Library" },
  { id: "lending",   label: "Lending" },
  { id: "following", label: "Following" },
  { id: "classroom", label: "Classroom" },
  { id: "help",      label: "Help" },
];

const Settings = () => {
  const { user, role, schoolId, gradeSpan } = useContext(UserContext);
  const [gradeSpanMsg, setGradeSpanMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { enterClassroomMode } = useClassroomMode();
  const [hasPin, setHasPin] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinMsg, setPinMsg] = useState("");
  const [absentDefault, setAbsentDefault] = useState(false);
  const [hideSchoolWide, setHideSchoolWide] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [schoolDetails, setSchoolDetails] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [nameSignOut, setNameSignOut]     = useState(false);
  const [specialist, setSpecialist]       = useState(false);
  const [signInRequiresId, setSignInRequiresId] = useState(false);
  const [showTripsCount, setShowTripsCount] = useState(true);
  const [allowCustomLocation, setAllowCustomLocation] = useState(true);
  const [idScheme, setIdScheme]           = useState("");
  const [signOutRoom, setSignOutRoom] = useState("");
  const [libraryEnabled, setLibraryEnabled] = useState(false);
  const [trackers, setTrackers]       = useState([]);
  const [hasSample, setHasSample] = useState(false);
  const [removingSample, setRemovingSample] = useState(false);
  const [showAddTracker, setShowAddTracker] = useState(false);
  const [newTrackerName, setNewTrackerName] = useState("");
  const [newTrackerFields, setNewTrackerFields] = useState([""]);
  const [newTrackerColor, setNewTrackerColor] = useState(TRACKER_COLORS[0]);
  // Allow deep-linking to a specific tab, e.g. navigate("/settings", { state: { tab: "classroom" } }).
  const requestedTab = location.state?.tab;
  const [activeTab, setActiveTab] = useState(
    SETTINGS_TABS.some((t) => t.id === requestedTab) ? requestedTab : "account"
  );
  // Admin gets the extra Safety + Admin tabs. Rendered as a tab bar on wide
  // screens and a dropdown on narrow ones (see CSS) so the ~8 tabs don't wrap
  // into a scattered grid on a phone.
  const visibleTabs = role === "admin"
    ? [...SETTINGS_TABS, { id: "safety", label: "Safety" }, { id: "admin", label: "Admin" }]
    : SETTINGS_TABS;

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user) {
        try {
          const userRef = doc(firestore, "users", user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            setDisplayName(userData.displayName || "");
            setEmail(userData.email || "");
            setNameSignOut(userData.nameSignOut === true);
            setSpecialist(userData.specialistMode === true);
            setSignInRequiresId(userData.signInRequiresId === true);
            // Unset defaults to ON (the count has always shown until now).
            setShowTripsCount(userData.showTripsCount !== false);
            setAllowCustomLocation(userData.allowCustomLocation !== false);
            setIdScheme(userData.studentIdScheme || "");
            setSignOutRoom(userData.signOutRoom || "");
            setLibraryEnabled(userData.libraryEnabled === true);
            setHasPin(!!userData.classroomPin);
            setAbsentDefault(userData.absentBoardDefault === true);
            setHideSchoolWide(userData.hideSchoolWideAttendance === true);

            if (userData.schoolId) {
              const schoolRef = doc(firestore, "schools", userData.schoolId);
              const schoolDoc = await getDoc(schoolRef);

              if (schoolDoc.exists()) {
                const schoolData = schoolDoc.data();
                // Stack name / address / city-state-zip so the town + state get their
                // own line and never force horizontal scroll on a phone.
                const formattedDetails = `${schoolData.schoolName}\n${schoolData.address}\n${schoolData.city}, ${schoolData.state} ${schoolData.zipcode}`;
                setSchoolDetails(formattedDetails);
                setSelectedSchool({
                  id: userData.schoolId,
                  ...schoolData,
                });
              } else {
                setSchoolDetails("School information not found.");
              }
            } else {
              setSchoolDetails("No school assigned.");
            }
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      }
    };

    fetchUserDetails();
  }, [user]);

  const handleAddSchool = () => setShowAddSchoolModal(true);

  useEffect(() => {
    if (!user?.uid) return;
    return onSnapshot(doc(firestore, "users", user.uid), snap => {
      setTrackers(snap.data()?.lendingTrackers || []);
      setHasSample(!!snap.data()?.sampleSeed);
    });
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Factory-reset the seeded demo classroom (also reachable from its class card).
  const handleResetSample = async () => {
    if (!window.confirm(
      "Remove the sample class and ALL of its demo data — sample students, sign-out " +
      "history, today's attendance, the sample library books and the Chromebooks " +
      "lending tracker? This can't be undone."
    )) return;
    setRemovingSample(true);
    try {
      await removeSampleClassroom(user.uid);
    } catch (error) {
      console.error("Error removing sample data:", error);
      window.alert("Couldn't remove the sample data. Please try again.");
    } finally {
      setRemovingSample(false);
    }
  };

  const addTracker = async () => {
    if (!newTrackerName.trim()) return;
    const tracker = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
      name: newTrackerName.trim(),
      color: newTrackerColor,
      fields: newTrackerFields.filter(f => f.trim()).map(label => ({ label: label.trim() })),
    };
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        lendingTrackers: [...trackers, tracker],
      });
      setNewTrackerName(""); setNewTrackerFields([""]); setNewTrackerColor(TRACKER_COLORS[0]); setShowAddTracker(false);
    } catch (err) { console.error("Error adding tracker:", err); }
  };

  const setTrackerColor = async (tracker, color) => {
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        lendingTrackers: trackers.map(t => (t.id === tracker.id ? { ...t, color } : t)),
      });
    } catch (err) { console.error("Error setting tracker color:", err); }
  };

  // Per-tracker overdue threshold in days (e.g. Chromebooks = 1 overnight,
  // library = 14). Clamped to a sane minimum of 1 day.
  const setTrackerOverdue = async (tracker, days) => {
    const n = Math.max(1, parseInt(days, 10) || 14);
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        lendingTrackers: trackers.map(t => (t.id === tracker.id ? { ...t, overdueDays: n } : t)),
      });
    } catch (err) { console.error("Error setting tracker overdue days:", err); }
  };

  const deleteTracker = async (tracker) => {
    if (!window.confirm(`Delete "${tracker.name}"? Existing checkout records won't be affected.`)) return;
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        lendingTrackers: trackers.filter(t => t.id !== tracker.id),
      });
    } catch (err) { console.error("Error deleting tracker:", err); }
  };

  const handleToggleLibrary = async () => {
    const newValue = !libraryEnabled;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { libraryEnabled: newValue });
      setLibraryEnabled(newValue);
    } catch (err) { console.error("Error toggling library:", err); }
  };

  // How the teacher identifies students (set during onboarding; editable here).
  // "none" → name-grid sign-out; "school"/"class" → ID form. Keeps nameSignOut in
  // sync; the directory default for Add Students is derived from this scheme.
  const handleSetIdScheme = async (scheme) => {
    const nameMode = scheme === "none";
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        studentIdScheme: scheme,
        nameSignOut: nameMode,
      });
      setIdScheme(scheme);
      setNameSignOut(nameMode);
    } catch (err) {
      console.error("Error saving student-ID scheme:", err);
    }
  };

  const handleToggleNameSignOut = async () => {
    const newValue = !nameSignOut;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { nameSignOut: newValue });
      setNameSignOut(newValue);
    } catch (err) {
      console.error("Error updating sign-out mode:", err);
    }
  };

  const handleToggleSpecialist = async () => {
    const newValue = !specialist;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { specialistMode: newValue });
      setSpecialist(newValue);
    } catch (err) {
      console.error("Error updating specialist mode:", err);
    }
  };

  const handleToggleShowTripsCount = async () => {
    const newValue = !showTripsCount;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { showTripsCount: newValue });
      setShowTripsCount(newValue);
    } catch (err) {
      console.error("Error updating trips-count visibility:", err);
    }
  };

  const handleToggleAllowCustomLocation = async () => {
    const newValue = !allowCustomLocation;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { allowCustomLocation: newValue });
      setAllowCustomLocation(newValue);
    } catch (err) {
      console.error("Error updating custom-location setting:", err);
    }
  };

  const handleToggleSignInRequiresId = async () => {
    const newValue = !signInRequiresId;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { signInRequiresId: newValue });
      setSignInRequiresId(newValue);
    } catch (err) {
      console.error("Error updating sign-in requirement:", err);
    }
  };

  const handleSaveSignOutRoom = async () => {
    try {
      await updateDoc(doc(firestore, "users", user.uid), { signOutRoom: signOutRoom.trim() });
    } catch (err) {
      console.error("Error saving sign-out room:", err);
    }
  };

  // Classroom Mode: a 4-digit PIN (stored on the user doc) gates exiting the locked
  // classroom view. Must be set before the device can be put into Classroom Mode.
  const handleSavePin = async () => {
    if (!/^\d{4}$/.test(pinInput)) {
      setPinMsg("PIN must be exactly 4 digits.");
      return;
    }
    try {
      await updateDoc(doc(firestore, "users", user.uid), { classroomPin: pinInput });
      setHasPin(true);
      setPinInput("");
      setPinMsg("PIN saved.");
    } catch (err) {
      console.error("Error saving classroom PIN:", err);
      setPinMsg("Couldn't save PIN. Please try again.");
    }
  };

  const handleEnterClassroom = () => {
    enterClassroomMode();
    navigate("/classsignoutpage");
  };

  const handleToggleAbsentDefault = async () => {
    const v = !absentDefault;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { absentBoardDefault: v });
      setAbsentDefault(v);
    } catch (err) {
      console.error("Error saving absent-board default:", err);
    }
  };

  const handleToggleHideSchoolWide = async () => {
    const v = !hideSchoolWide;
    try {
      await updateDoc(doc(firestore, "users", user.uid), { hideSchoolWideAttendance: v });
      setHideSchoolWide(v);
    } catch (err) {
      console.error("Error saving attendance-dropdown preference:", err);
    }
  };

  // Persist the school's grade span (drives grade pickers + who graduates out).
  // Admin is in the school's teachers[], so the existing schools update rule covers it.
  const handleSetGradeSpan = async (presetKey) => {
    const preset = GRADE_SPAN_PRESETS.find(p => p.key === presetKey);
    if (!preset || !schoolId) return;
    setGradeSpanMsg("");
    try {
      await updateDoc(doc(firestore, "schools", schoolId), {
        gradeSpan: { key: preset.key, low: preset.low, high: preset.high },
      });
      setGradeSpanMsg(`Saved — ${preset.label}.`);
    } catch (err) {
      console.error("Error saving grade span:", err);
      setGradeSpanMsg("Couldn't save. Try again.");
    }
  };

  const handleSchoolSelection = (school) => {
    if (school) {
      setSelectedSchool(school);

      // Format the school details string, handling undefined fields gracefully
      const formattedDetails = `${school.schoolName || "N/A"}\n${
        school.address || "N/A"
      }\n${school.city || "N/A"}, ${school.state || "N/A"} ${
        school.zipcode || "N/A"
      }`;
      setSchoolDetails(formattedDetails);
    } else {
      setSchoolDetails("No school assigned.");
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    setFeedback("");

    try {
      const userRef = doc(firestore, "users", user.uid);

      // Update display name
      if (displayName !== user.displayName) {
        await updateProfile(auth.currentUser, { displayName });
        await updateDoc(userRef, { displayName });
      }

      // Update email
      if (email !== user.email) {
        await updateEmail(auth.currentUser, email);
        await updateDoc(userRef, { email });
      }

      // Update password
      if (password) {
        await updatePassword(auth.currentUser, password);
      }

      // Changing school is now a REQUEST → a school admin must approve. The user
      // stays in their current school until then (auto-moved once approved).
      let joinMsg = "";
      if (selectedSchool) {
        await requestToJoinSchool({ uid: user.uid, user, school: selectedSchool });
        joinMsg = ` Your request to join ${selectedSchool.schoolName} was sent — a school admin will review it, and you'll be added automatically once approved.`;
      }

      setFeedback("Profile updated successfully!" + joinMsg);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setFeedback("Error updating profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <h1 style={{ marginBottom: 16 }}>Settings</h1>
      {/* Narrow screens: a dropdown instead of a wrapping tab grid. */}
      <select
        className="settings-tab-select"
        value={activeTab}
        onChange={(e) => setActiveTab(e.target.value)}
        aria-label="Settings section"
      >
        {visibleTabs.map(t => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>

      {/* Wide screens: the horizontal tab bar. */}
      <div className="settings-tabs">
        {visibleTabs.map(t => (
          <button
            key={t.id}
            className={`settings-tab${activeTab === t.id ? " active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "account" && (
      <div className="settings-section">
      <h2>Account Settings</h2>

      {/* Display Name */}
      <div className="settings-field">
        <label htmlFor="displayName">Display Name</label>
        {!isEditing ? (
          <p>{displayName || "N/A"}</p>
        ) : (
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        )}
      </div>

      {/* Email */}
      <div className="settings-field">
        <label htmlFor="email">Email</label>
        {!isEditing ? (
          <p>{email || "N/A"}</p>
        ) : (
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </div>

      {/* Password */}
      {isEditing && (
        <div className="settings-field">
          <label htmlFor="password">New Password</label>
          <input
            type="password"
            id="password"
            value={password}
            placeholder="Leave blank to keep current password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      )}

      {/* School Details */}
      <div className="settings-field">
        <label htmlFor="schoolDetails">School</label>
        {!isEditing ? (
          <div className="school-details">{schoolDetails || "N/A"}</div>
        ) : (
          <SchoolDropdown
            onSelectSchool={handleSchoolSelection} // Call handleSchoolSelection here
            onAddSchool={handleAddSchool}
          />
        )}
      </div>

      {/* Feedback */}
      {feedback && <div className="settings-feedback">{feedback}</div>}

      {/* Buttons */}
      <div className="settings-buttons">
        {!isEditing ? (
          <button onClick={() => setIsEditing(true)}>Edit Profile</button>
        ) : (
          <>
            <button onClick={handleUpdateProfile} disabled={loading}>
              {loading ? "Updating..." : "Save Changes"}
            </button>
            <button className="red-button" onClick={() => setIsEditing(false)}>
              Cancel
            </button>
          </>
        )}
      </div>

      {/* Sample classroom: factory-reset (only while the demo data is present) */}
      {hasSample && (
        <div className="settings-field-block" style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #eee" }}>
          <h3>Sample classroom</h3>
          <p className="settings-note">
            Your account still has the demo "✨ Sample Class" we set up so you could try
            everything. When you're ready for your real students, remove it and all of its
            sample data (students, sign-out history, attendance, library books and the
            Chromebooks tracker).
          </p>
          <button className="red-button" onClick={handleResetSample} disabled={removingSample}>
            {removingSample ? "Removing…" : "Remove sample class & data"}
          </button>
        </div>
      )}

      {/* Add School Modal */}
      {showAddSchoolModal && (
        <AddSchoolModal
          onClose={() => setShowAddSchoolModal(false)}
          onSchoolAdded={(newSchoolData) => {
            setSelectedSchool(newSchoolData);
            setShowAddSchoolModal(false);
          }}
        />
      )}
      </div>
      )}

      {activeTab === "library" && (
      <div className="settings-section">
        <h2>Library</h2>
        <p className="settings-intro">
          Track book checkouts and build a searchable catalog. Scan ISBN barcodes to auto-fill book details via Google Books.
        </p>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={libraryEnabled}
            onChange={handleToggleLibrary}
          />
          Enable library
        </label>
      </div>
      )}

      {activeTab === "signout" && (
      <div className="settings-section">
        <h2>Sign-Out Mode</h2>

        <div className="settings-field-block" style={{ marginTop: 0 }}>
          <label htmlFor="idScheme" className="settings-label">How you identify students</label>
          <p className="settings-note" style={{ marginBottom: "var(--space-2)" }}>
            Sets the sign-out screen and the default for adding students. School IDs are
            shared in the directory; class numbers stay private; “no number” uses a name grid
            and auto-assigns IDs behind the scenes.
          </p>
          <select
            id="idScheme"
            value={idScheme}
            onChange={e => handleSetIdScheme(e.target.value)}
            className="settings-input"
            style={{ width: "auto" }}
          >
            <option value="" disabled>Select…</option>
            <option value="school">School / district ID numbers</option>
            <option value="class">My own class numbers (1–24)</option>
            <option value="none">No ID numbers (name grid)</option>
          </select>
        </div>

        <p className="settings-intro">
          Name mode shows a grid of student names — students click their own name then pick a destination. ID mode uses the standard student ID number form.
        </p>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={nameSignOut}
            onChange={handleToggleNameSignOut}
          />
          Use name-based sign-out
        </label>

        <div className="settings-field-block">
          <p className="settings-intro">
            <strong>Specialist mode</strong> — for art, music, gym, the nurse, the librarian, or any
            staff who work across the whole school. Your sign-out and attendance screens show a
            dropdown of <strong>every class in the school</strong>, so you can pick any classroom and
            sign a student out by name. (Your school admin can also assign this as a role.)
          </p>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={specialist}
              onChange={handleToggleSpecialist}
            />
            I'm a specialist — show me all classes in the school
          </label>
        </div>

        <div className="settings-field-block">
          <p className="settings-intro">
            When enabled, students must enter their ID number to sign back in — confirming it's
            really them. Signing out is unaffected. Students with no ID on file can still sign in
            with one click.
          </p>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={signInRequiresId}
              onChange={handleToggleSignInRequiresId}
            />
            Require student ID to sign back in
          </label>
        </div>

        <div className="settings-field-block">
          <p className="settings-intro">
            Shows a small 🔁 count on the sign-out screen for a student who's already been
            out today, so you can spot repeat trips at a glance. Turn it off to hide it.
          </p>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={showTripsCount}
              onChange={handleToggleShowTripsCount}
            />
            Show each student's “times out today” count
          </label>
        </div>

        <div className="settings-field-block">
          <p className="settings-intro">
            When on, choosing “Other” lets a student type where they're going. Turn it off
            and “Other” simply records “Other” with no text box — so students can't type
            anything inappropriate.
          </p>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={allowCustomLocation}
              onChange={handleToggleAllowCustomLocation}
            />
            Let students type a custom “Other” destination
          </label>
        </div>

        <div className="settings-field-block">
          <label htmlFor="signOutRoom" className="settings-label">
            Room number
          </label>
          <p className="settings-note" style={{ marginBottom: "var(--space-2)" }}>
            Shown on the school-wide sign-out board so the office knows where a student left from.
            Set once here — students can't change it from the sign-out screen. Leave blank to omit.
          </p>
          <input
            id="signOutRoom"
            type="text"
            value={signOutRoom}
            onChange={e => setSignOutRoom(e.target.value)}
            onBlur={handleSaveSignOutRoom}
            placeholder="e.g. 204"
            className="settings-input"
            style={{ width: 140 }}
          />
        </div>
      </div>
      )}

      {activeTab === "lending" && (
      <div className="settings-section">
        <h2>Lending Trackers</h2>
        <p className="settings-intro">
          Create trackers for anything students borrow — Chromebooks, calculators, books, pencils, etc.
        </p>

        {trackers.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {trackers.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap" }}>
                <span style={{ width: 14, height: 14, borderRadius: "50%", background: t.color || TRACKER_COLORS[0], flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 150 }}>
                  <span style={{ fontWeight: 600 }}>{t.name}</span>
                  {t.fields.length > 0 && (
                    <span style={{ color: "var(--color-secondary)", fontSize: 13, marginLeft: 8 }}>
                      Fields: {t.fields.map(f => f.label).join(", ")}
                    </span>
                  )}
                  {t.fields.length === 0 && (
                    <span style={{ color: "var(--color-secondary)", fontSize: 13, marginLeft: 8 }}>
                      No extra fields
                    </span>
                  )}
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--color-secondary)" }}>
                  Overdue after
                  <input
                    type="number"
                    min="1"
                    key={`${t.id}-${t.overdueDays ?? 14}`}
                    defaultValue={t.overdueDays ?? 14}
                    onBlur={(e) => setTrackerOverdue(t, e.target.value)}
                    title="Days out before this item is flagged Overdue"
                    style={{ width: 56, padding: "2px 6px" }}
                  />
                  days
                </label>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {TRACKER_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setTrackerColor(t, c)}
                      title="Set color"
                      style={{
                        width: 18, height: 18, borderRadius: "50%", background: c,
                        cursor: "pointer", padding: 0,
                        border: (t.color || TRACKER_COLORS[0]) === c ? "2px solid #333" : "2px solid #fff",
                        boxShadow: "0 0 0 1px #ddd",
                      }}
                    />
                  ))}
                </div>
                <button
                  onClick={() => deleteTracker(t)}
                  style={{ background: "none", border: "1px solid #ddd", color: "var(--color-secondary)", padding: "4px 12px", fontSize: 13, cursor: "pointer", borderRadius: 4 }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {!showAddTracker ? (
          <button onClick={() => setShowAddTracker(true)}>Add Tracker</button>
        ) : (
          <div style={{ padding: 16, border: "1px solid #e0e0e0", borderRadius: 8, background: "#fafafa" }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Name</label>
              <input
                type="text"
                placeholder="e.g. Chromebooks, Calculators, Class Library"
                value={newTrackerName}
                onChange={e => setNewTrackerName(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Color</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {TRACKER_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewTrackerColor(c)}
                    title="Pick color"
                    style={{
                      width: 24, height: 24, borderRadius: "50%", background: c,
                      cursor: "pointer", padding: 0,
                      border: newTrackerColor === c ? "3px solid #333" : "3px solid #fff",
                      boxShadow: "0 0 0 1px #ddd",
                    }}
                  />
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 4, fontSize: 14, fontWeight: 600 }}>
                Fields{" "}
                <span style={{ color: "var(--color-secondary)", fontWeight: 400 }}>
                  (optional — what info to record per checkout)
                </span>
              </label>
              {newTrackerFields.map((field, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <input
                    type="text"
                    placeholder="e.g. Chromebook #, Book Title, Condition"
                    value={field}
                    onChange={e => {
                      const updated = [...newTrackerFields];
                      updated[i] = e.target.value;
                      setNewTrackerFields(updated);
                    }}
                    style={{ flex: 1 }}
                  />
                  {newTrackerFields.length > 1 && (
                    <button
                      onClick={() => setNewTrackerFields(newTrackerFields.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-secondary)", fontSize: 20, padding: "0 4px", lineHeight: 1 }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setNewTrackerFields([...newTrackerFields, ""])}
                style={{ background: "none", border: "none", color: "var(--color-primary)", cursor: "pointer", fontSize: 13, padding: 0, marginTop: 4 }}
              >
                + Add field
              </button>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addTracker} disabled={!newTrackerName.trim()}>Save</button>
              <button
                onClick={() => { setShowAddTracker(false); setNewTrackerName(""); setNewTrackerFields([""]); setNewTrackerColor(TRACKER_COLORS[0]); }}
                style={{ background: "none", border: "1px solid #ddd", color: "var(--color-secondary)" }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {activeTab === "following" && (
      <div className="settings-section">
        <h2>Following</h2>
        <p className="settings-intro">
          Follow other teachers to see their active sign-outs on the sign-out screen when students are out.
        </p>
        <ManageFollowing />
      </div>
      )}

      {activeTab === "classroom" && (
      <div className="settings-section">
        <h2>Classroom Mode</h2>
        <p className="settings-intro">
          Lock <strong>this device</strong> down to the student-safe screens — Sign-Out, Attendance,
          and Library/Lending. Check List/Emergency, Settings, rosters, and account log-out are
          hidden so students can't reach them. It's a per-device setting (it won't follow you to
          your laptop), and a 4-digit PIN is required to exit.
        </p>

        <div className="settings-field-block">
          <label htmlFor="classroomPin" className="settings-label">
            Exit PIN {hasPin && <span style={{ color: "var(--color-secondary)", fontWeight: 400, fontSize: "var(--text-xs)" }}>(set — enter a new one to change)</span>}
          </label>
          <div className="settings-actions">
            <input
              id="classroomPin"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value.replace(/\D/g, "")); setPinMsg(""); }}
              placeholder={hasPin ? "New 4-digit PIN" : "4-digit PIN"}
              className="settings-input"
              style={{ width: 140, letterSpacing: "0.3em" }}
            />
            <button onClick={handleSavePin} disabled={pinInput.length !== 4}>
              {hasPin ? "Change PIN" : "Set PIN"}
            </button>
          </div>
          {pinMsg && <p className={`settings-status ${pinMsg.includes("saved") ? "is-ok" : "is-error"}`}>{pinMsg}</p>}
        </div>

        <div className="settings-field-block">
          <button
            onClick={handleEnterClassroom}
            disabled={!hasPin}
            title={hasPin ? "" : "Set an exit PIN first"}
          >
            Enter Classroom Mode on this device
          </button>
          {!hasPin && (
            <p className="settings-note">
              Set an exit PIN above first.
            </p>
          )}
          {hasPin && (
            <p className="settings-note">
              Shortcut: press <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>Shift</kbd> + <kbd>L</kbd> from anywhere to lock this device into Classroom Mode.
            </p>
          )}
        </div>
      </div>
      )}

      {activeTab === "help" && <SupportSettings schoolName={selectedSchool?.schoolName} />}

      {activeTab === "safety" && <SafetySettings />}

      {activeTab === "admin" && (
      <div className="settings-section">
        <h2>Admin</h2>
        <p className="settings-intro">
          Preferences for school admins.
        </p>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={absentDefault}
            onChange={handleToggleAbsentDefault}
          />
          Open Attendance on the school-wide absent board
        </label>
        <p className="settings-note">
          When on, the Attendance page starts on “Absent — school-wide” instead of By class. You can still switch anytime.
        </p>

        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={hideSchoolWide}
            onChange={handleToggleHideSchoolWide}
          />
          Show only my classes in the attendance dropdown
        </label>
        <p className="settings-note">
          When on, the Attendance class picker lists only your own classes instead of every class in the school. The “Absent — school-wide” board is unaffected.
        </p>

        <div className="settings-field-block">
          <h3 style={{ marginTop: 0 }}>Grade range</h3>
          <p className="settings-note" style={{ marginBottom: "var(--space-3)" }}>
            The grades your school serves. This sets the grade choices when adding students and
            determines who graduates out at year-end. Each student is tracked internally by
            graduation year, so grades advance automatically each school year.
          </p>
          <label className="settings-toggle" style={{ cursor: "default" }}>
            School grades
            <select
              value={gradeSpan?.key || ""}
              onChange={e => handleSetGradeSpan(e.target.value)}
              className="settings-input"
              style={{ width: "auto" }}
            >
              <option value="" disabled>Select a range…</option>
              {GRADE_SPAN_PRESETS.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </label>
          {gradeSpanMsg && (
            <p className="settings-status is-ok">{gradeSpanMsg}</p>
          )}
        </div>

        <LunchSettings />
      </div>
      )}
    </div>
  );
};

export default Settings;
